import React, { useEffect, useRef, useId } from 'react';
import mermaid from 'mermaid';

interface TreeNode {
  text: string;
  children: TreeNode[];
  depth: number;
  id?: string;
}

interface TreeDiagramProps {
  content: string;
}

/**
 * Parses ASCII tree content into a tree structure
 */
function parseTree(content: string): TreeNode[] {
  const lines = content.split('\n').filter(line => line.trim());
  const roots: TreeNode[] = [];
  const stack: { node: TreeNode; indent: number }[] = [];
  let nodeCounter = 0;

  for (const line of lines) {
    // Calculate the indentation level by counting leading spaces and tree chars
    const match = line.match(/^([\s│├└─]*)/);
    const prefix = match ? match[1] : '';
    
    // Calculate depth based on tree structure chars
    const treeCharCount = (prefix.match(/[├└│]/g) || []).length;
    const depth = treeCharCount;
    
    // Extract the actual text content
    let text = line
      .replace(/^[\s│]*[├└]─\s*/, '') // Remove leading tree chars
      .replace(/^[\s│]+/, '')          // Remove only vertical bars
      .trim();
    
    // If line doesn't have tree chars, it's a root
    if (!line.includes('├') && !line.includes('└')) {
      text = line.trim();
    }

    if (!text) continue;

    const node: TreeNode = { 
      text, 
      children: [], 
      depth,
      id: `node${nodeCounter++}`
    };

    // Find the parent node
    while (stack.length > 0 && stack[stack.length - 1].indent >= depth) {
      stack.pop();
    }

    if (stack.length === 0) {
      roots.push(node);
    } else {
      stack[stack.length - 1].node.children.push(node);
    }

    stack.push({ node, indent: depth });
  }

  return roots;
}

/**
 * Escapes text for Mermaid syntax
 */
function escapeForMermaid(text: string): string {
  return text
    .replace(/"/g, "'")
    .replace(/[[\](){}]/g, ' ')
    .replace(/→/g, '->')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .trim();
}

/**
 * Converts parsed tree nodes to Mermaid flowchart syntax
 */
function convertToMermaid(roots: TreeNode[]): string {
  const lines: string[] = ['graph TD'];
  const nodeDefinitions: string[] = [];
  const connections: string[] = [];

  function processNode(node: TreeNode, parentId?: string) {
    const escapedText = escapeForMermaid(node.text);
    // Use rounded rectangle for all nodes
    nodeDefinitions.push(`    ${node.id}["${escapedText}"]`);
    
    if (parentId) {
      connections.push(`    ${parentId} --> ${node.id}`);
    }

    for (const child of node.children) {
      processNode(child, node.id);
    }
  }

  for (const root of roots) {
    processNode(root);
  }

  // Add node definitions first, then connections
  lines.push(...nodeDefinitions);
  lines.push(...connections);

  return lines.join('\n');
}

/**
 * Visual flow diagram component that converts ASCII tree structures to Mermaid flowcharts
 */
export default function TreeDiagram({ content }: TreeDiagramProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const uniqueId = useId().replace(/:/g, '');
  const roots = parseTree(content);

  useEffect(() => {
    if (roots.length === 0 || !containerRef.current) return;

    const mermaidCode = convertToMermaid(roots);
    const mermaidDiv = containerRef.current.querySelector('.mermaid');
    
    if (mermaidDiv) {
      mermaidDiv.textContent = mermaidCode;
      mermaidDiv.removeAttribute('data-processed');
      
      mermaid.run({
        nodes: [mermaidDiv as HTMLElement],
      }).catch(console.error);
    }
  }, [content, roots]);

  if (roots.length === 0) {
    return (
      <pre className="bg-muted p-4 rounded-lg text-sm font-mono overflow-x-auto">
        {content}
      </pre>
    );
  }

  const mermaidCode = convertToMermaid(roots);

  return (
    <div 
      ref={containerRef}
      className="flow-diagram my-6 p-4 bg-card border border-border rounded-lg overflow-x-auto"
    >
      <div 
        className="mermaid"
        data-diagram-id={uniqueId}
      >
        {mermaidCode}
      </div>
    </div>
  );
}

/**
 * Detects if content is an ASCII tree diagram
 */
export function isTreeDiagram(content: string): boolean {
  // Check if content contains tree structure characters
  const treeChars = ['├─', '└─', '│'];
  const lines = content.split('\n');
  
  // Need at least 2 lines with tree characters to be considered a tree
  let treeLineCount = 0;
  for (const line of lines) {
    if (treeChars.some(char => line.includes(char))) {
      treeLineCount++;
      if (treeLineCount >= 2) return true;
    }
  }
  
  return false;
}
